import type {
  Aufrisszelle,
  Bericht,
  Berichtszeile,
  Gliederungsbild,
  Gliederungsknoten,
} from './typen'

/**
 * Ein Berichtsbildschirm ohne gerechneten Abschluss (2.12 Abschnitt 3.11,
 * R717–R719).
 *
 * **Die Gliederung ist da, auch wenn nichts gerechnet ist.** Sie steht im
 * Positionsplan und in den Kontozuordnungen, und ein Bildschirm, der davon
 * nichts weiß, verschweigt die Arbeit, die jemand gepflegt hat. Der
 * Gliederungsdienst liefert sie seit dem 2026-09-11 für den Zuordnungstisch
 * (R307) — hier wird daraus ein Berichtssatz.
 *
 * **Warum ein Berichtssatz und kein eigenes Bauteil:** Aufklappen, Einrücken,
 * Suchen, Spaltenbreiten und das Kontextmenü zum Anlegen hängen alle an
 * diesem einen Bauteil. Ein zweites daneben wäre dieselbe Tabelle in
 * schlechter — und beim ersten Umbau liefen die beiden auseinander.
 *
 * **Die Wertspalten bleiben leer, nicht null** (R718). Das kostet hier keine
 * Zeile: Die Wertzelle zeigt eine Null ohnehin als leere Zelle (`Wert` in
 * `Bericht.tsx`). Eine Null, die dastünde, behauptete ein Rechenergebnis.
 */

/** Welche Positionsarten in welchem Bericht stehen. */
const ARTEN: Record<string, string[]> = {
  bilanz: ['aktiva', 'passiva'],
  guv: ['ertrag', 'aufwand'],
}

/**
 * Baut aus der Gliederung einen Berichtssatz ohne Zahlen.
 *
 * @param art `bilanz` oder `guv` — das Gliederungsbild trägt beide.
 */
export function ausGliederung(bild: Gliederungsbild, art: string, ledger: string): Bericht {
  const gewuenscht = ARTEN[art] ?? []
  const knoten = bild.knoten.filter((k) => gewuenscht.includes(k.art))

  return {
    art,
    // **Kein Kreis, kein Lauf, kein Prüfcode** (R718): Wo keine Zahl steht,
    // ist auch keiner zu nennen. Die Fußzeile zeigt die Laufnummer ohnehin
    // nur, wenn ein Lauf geladen ist.
    kreis: '',
    ledger,
    periode: bild.periode,
    waehrung: '',
    laufId: 0,
    ergebnisHash: '',
    stand: 0,
    gerechnetAm: '',
    // **Nichts geht auf und nichts geht schief.** Ohne Zahlen gibt es keine
    // Differenz, über die zu berichten wäre.
    gehtAuf: true,
    differenz: 0,
  // Ohne Lauf gerechnet gibt es hier nicht: Die Gliederungsansicht ist die
  // gerechnete Form ohne Zahlen (R717).
  herkunft: 'gerechnet' as const,
    schichten: [],
    struktur: [],
    stufen: [],
    zeilen: knoten.map((k, i) => zeile(k, knoten[i + 1] ?? null)),
  }
}

/**
 * Eine Zeile ohne Zahlen — mit ihren Konten als Aufrisszellen darunter.
 *
 * **Die Konten stehen da, obwohl auf ihnen nichts gebucht ist** (R505, R717).
 * Das ist dieselbe Entscheidung wie im gerechneten Bericht: Die Gliederung
 * zeigt, was da ist, und nicht, was zufällig einen Betrag trägt.
 */
function zeile(k: Gliederungsknoten, naechster: Gliederungsknoten | null): Berichtszeile {
  return {
    position: k.schluessel,
    code: k.code,
    namen: k.namen,
    art: k.art,
    ebene: k.ebene,
    istSumme: k.istSumme,
    // **Welche Summe das Ergebnis ist, sagt der Baum** (R484) — nicht der
    // Lauf. In der Bilanz sind das Aktiva und Passiva, in der GuV das
    // Konzernjahresergebnis.
    istErgebnis: k.istErgebnis ?? false,
    hatKonten: k.konten.length > 0,
    // **Unterpositionen erkennt man an der Einrückung der nächsten Zeile.**
    // Das Bild ist eine Tiefensuche: Was unter einer Position hängt, steht
    // direkt darunter und eine Ebene tiefer.
    hatKinder: naechster !== null && naechster.ebene > k.ebene,
    istRechenposition: k.istRechenposition,
    /*
      **Der Rang ordnet die Summenzeilen optisch** (R570) — Abschnitt,
      Zwischensumme, unterste Summe.

      *Berichtigt am 2026-09-24 auf den Befund des Auftraggebers.* Hier stand
      `null` mit der Begründung, der Rang folge aus einem Baum, den der
      Berichtsdienst auflöst. Das stimmt — und der Gliederungsdienst löst
      denselben Baum auf. Die Folge war sichtbar: Im Bericht ohne Lauf sahen
      Abschnitt, Zwischensumme und Posten gleich aus.

      **`null` ist hier kein leerer Wert, sondern eine Aussage:** Die
      Oberfläche liest `null` als „keine Hervorhebung" und `undefined` als
      „nicht mitgeliefert, leite es aus `istSumme` ab" (`rangklasse`). Ein
      Posten trägt deshalb weiterhin `null`.
    */
    rang: k.rang ?? null,
    hb2: 0,
    kapKo: 0,
    schuKo: 0,
    aeKo: 0,
    zwErgEUv: 0,
    zwErgEAv: 0,
    konzern: 0,
    beitraege: k.konten.map(kontozelle),
    schichten: [],
    stufenkonso: [],
    saldowechsel: 0,
  }
}

function kontozelle(konto: { schluessel: string }): Aufrisszelle {
  return {
    einheit: null,
    konto: konto.schluessel,
    hb2: 0,
    kapKo: 0,
    schuKo: 0,
    aeKo: 0,
    zwErgEUv: 0,
    zwErgEAv: 0,
    konzern: 0,
    schichten: [],
    stufenkonso: [],
  }
}
